// Import our properly initialized pdfMake
import pdfMake from './pdfmake-setup';
import { TDocumentDefinitions, Content } from 'pdfmake/interfaces';
import { models, VisualDescriptor } from 'powerbi-client';
import { VISUAL_IDS } from '../../../../constants/slicers';

// Import image paths - these will be converted to data URLs
import backgroundImage1withPaint from '../../../../assets/1wp.jpg';
import backgroundImage1withoutPaint from '../../../../assets/1wop.jpg';
import backgroundImage2 from '../../../../assets/2.jpg';

import backgroundImage1withPaintNoFee from '../../../../assets/no_saas/1wp.jpg';
import backgroundImage1withoutPaintNoFee from '../../../../assets/no_saas/1wop.jpg';
import backgroundImage2NoFee from '../../../../assets/no_saas/2.jpg';

import { formatNumberWithCommas } from '../../../../utils';

// Helper function to convert image URL to data URL
const getDataUrl = (imgPath: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    fetch(imgPath)
      .then(response => response.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });
};

// Image cache to avoid loading the same image multiple times
const imageCache: { [key: string]: string } = {};

class PDFModel {
  PaintAddons: string[][];
  CoreServices: string[][];
  ExtraUnitConditions: string[][];
  ExtraRoom: string;
  Townhome: string;
  Discount: string;
  Markets: string;
  MarketCount: string;
  SaasUnitFee: string;
  SaasContractedUnits: string;
  SaasTotal: string;
  SaasBillingCycle: string;
  PlatformFeeEnabled: boolean;

  Repair: string[][];
  Resurfacing: string[][];
  Maintenance: string[][];
  CarpetCleaning: string[][];
  Cleaning: string[][];
  Painting: string[][];

  constructor() {
    this.PlatformFeeEnabled = true;

    this.PaintAddons = [];
    this.CoreServices = [];
    this.ExtraUnitConditions = [];
    this.ExtraRoom = '';
    this.Townhome = '';
    this.Discount = '';
    this.Markets = '';
    this.MarketCount = '';
    this.SaasUnitFee = '';
    this.SaasContractedUnits = '';
    this.SaasTotal = '';
    this.SaasBillingCycle = '';

    this.Repair = [];
    this.Resurfacing = [];
    this.Maintenance = [];
    this.CarpetCleaning = [];
    this.Cleaning = [];
    this.Painting = [];
  }

  async maybeAddTable(visual: VisualDescriptor): Promise<void> {
    const id = visual.name;
    let value = null;

    switch (id) {
      case VISUAL_IDS.PAINT_ADDONS:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.PaintAddons = this.splitData(value);
        break;
      case VISUAL_IDS.CORE_SERVICES:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.CoreServices = this.splitData(value);
        break;
      case VISUAL_IDS.EXTRA_UNIT_CONDITIONS:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.ExtraUnitConditions = this.splitData(value);
        break;
      case VISUAL_IDS.EXTRA_ROOM:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.ExtraRoom = value.trim();
        break;
      case VISUAL_IDS.TOWNHOME:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.Townhome = value.trim();
        break;
      case VISUAL_IDS.DISCOUNT:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.Discount = value.trim().split('\r\n')[1];
        break;
      case VISUAL_IDS.MARKET_COUNT:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.MarketCount = value.trim().split('\r\n')[1].replace(/"/g, '');
        break;
      case VISUAL_IDS.MARKETS:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.Markets = value.trim().split('\r\n')[1].replace(/"/g, '') || '(Blank)';
        break;
      case VISUAL_IDS.REPAIR:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.Repair = this.splitDataPage2(value);
        break;
      case VISUAL_IDS.RESURFACING:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.Resurfacing = this.splitDataPage2(value);
        break;
      case VISUAL_IDS.MAINTENANCE:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.Maintenance = this.splitDataPage2(value);
        break;
      case VISUAL_IDS.CARPET_CLEANING:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.CarpetCleaning = this.splitDataPage2(value);
        break;
      case VISUAL_IDS.CLEANING:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.Cleaning = this.splitDataPage2(value);
        break;
      case VISUAL_IDS.PAINTING:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.Painting = this.splitDataPage2(value);
        break;
      case VISUAL_IDS.SAAS_UNIT_FEE_CARD:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.SaasUnitFee = value.trim().split('\r\n')[1].replace(/"/g, '');
        break;
      case VISUAL_IDS.SAAS_UNIT_COUNT_CARD:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.SaasContractedUnits = value.trim().split('\r\n')[1].replace(/"/g, '');
        break;
      case VISUAL_IDS.SAAS_BILLING_CYCLE:
        const billingCycleValue = ((await visual.getSlicerState()).filters[0] as any).values[0];
        this.SaasBillingCycle = billingCycleValue.toString();
        break;
      case VISUAL_IDS.SAAS_TOTAL_CARD:
        value = (await visual.exportData(models.ExportDataType.Summarized)).data;
        this.SaasTotal = value.trim().split('\r\n')[1].replace('$', '');
        break;
      case VISUAL_IDS.SAAS_FEE_ENABLED.REPORT1:
        const feeEnabledValue = ((await visual.getSlicerState()).filters[0] as any).values[0];
        this.PlatformFeeEnabled = feeEnabledValue === 'Yes';
        break;
      default:
        // this.addAny(value);
        break;
    }
  }

  splitData(value: string) {
    // Split the CSV data into lines
    const lines = value.trim().split('\r\n');
    const dataRows = lines.slice(1).map(line => line.split(',').slice(1, -1));
    return dataRows;
  }

  splitDataPage2(value: string) {
    // Split the CSV data into lines
    const lines = value.trim().split('\r\n');
    const dataRows = lines.slice(1).map(line => line.split(','));
    return dataRows;
  }

  splitSaasData(value: string) {
    // Split the CSV data into lines
    const lines = value.trim().split('\r\n');
    const dataRows = lines.slice(1).map(line => line.split(','));
    return dataRows;
  }

  // Helper function to format table data with alignment
  formatTableWithAlignment(data: string[][]) {
    return data.map(row =>
      row.map((cell, index) => ({
        text: cell,
        alignment: index === 0 ? 'left' : 'right',
      }))
    );
  }

  async export(onExported: () => void, paintProvided: boolean, exportName: string, openInNewTab: boolean = false) {
    try {
      // Only load background images if needed
      if (paintProvided) {
        try {
          imageCache['bg1_paint'] = await getDataUrl(
            this.PlatformFeeEnabled ? backgroundImage1withPaint : backgroundImage1withPaintNoFee
          );
        } catch (error) {
          console.error('Error loading bg1_paint image:', error);
        }
      }

      if (!paintProvided) {
        try {
          imageCache['bg1_nopaint'] = await getDataUrl(
            this.PlatformFeeEnabled ? backgroundImage1withoutPaint : backgroundImage1withoutPaintNoFee
          );
        } catch (error) {
          console.error('Error loading bg1_nopaint image:', error);
        }
      }

      try {
        imageCache['bg2'] = await getDataUrl(this.PlatformFeeEnabled ? backgroundImage2 : backgroundImage2NoFee);
      } catch (error) {
        console.error('Error loading bg2 image:', error);
      }

      // Convert column width strings to relative numbers for pdfmake
      const addonsWidths = [110, 49, 65, 65, 66, 65, 65];
      const saasWidths = [132, 58];
      const coreWidths = [102, 34, 45, 47, 45];
      const extraWidths = [90, 45, 43];
      const page2Width = [125, 33];

      let actualExtra = this.ExtraUnitConditions.map(row => [...row, '']);
      if (actualExtra.length > 0) {
        actualExtra[0][2] = this.ExtraRoom;
      }
      if (actualExtra.length > 3) {
        actualExtra[3][2] = this.Townhome;
      }

      // Create an array of content elements for the first page stack
      const firstPageContent: Content[] = [];
      const secondPageContent: Content[] = [];

      // Market info
      firstPageContent.push({
        text: this.Markets,
        fontSize: 12,
        bold: true,
        color: '#00ab9b',
        absolutePosition: { x: 373, y: 30 },
        alignment: 'center',
      });

      // Market Units
      firstPageContent.push({
        text: this.MarketCount,
        fontSize: 12,
        bold: true,
        color: '#00ab9b',
        absolutePosition: { x: 480, y: 30 },
        alignment: 'center',
      });

      // Define custom layout with no lines
      const tableLayout1 = {
        hLineWidth: function () {
          return 0;
        },
        vLineWidth: function () {
          return 0;
        },
        // Set row-specific heights
        paddingTop: function (i: number, node: any) {
          return 4.9;
        },
        paddingBottom: function (i: number, node: any) {
          return 4.9;
        },
        // Set column alignment - first column left, others right
        defaultBorder: false,
      };

      const tableLayoutSaas = {
        hLineWidth: function () {
          return 0;
        },
        vLineWidth: function () {
          return 0;
        },
        // Set row-specific heights
        paddingTop: function (i: number, node: any) {
          return 12;
        },
        paddingBottom: function (i: number, node: any) {
          return 9;
        },
        defaultBorder: false,
      };

      const tableLayout2 = {
        hLineWidth: function (i: number, node: any) {
          return 0;
        },
        vLineWidth: function () {
          return 0;
        },
        // Set row-specific heights
        paddingTop: function (i: number, node: any) {
          return 5.55;
        },
        paddingBottom: function (i: number, node: any) {
          return 5.45;
        },
        defaultBorder: false,
      };

      // Core Services Table
      firstPageContent.push({
        absolutePosition: { x: 27, y: 132 },
        table: {
          widths: coreWidths,
          body: this.formatTableWithAlignment(this.CoreServices),
        },
        layout: tableLayout1,
        color: '#071738',
        fontSize: 8,
        bold: true,
      });

      // Extra Unit Conditions Table
      firstPageContent.push({
        absolutePosition: { x: 367, y: 286 },
        table: {
          widths: extraWidths,
          body: this.formatTableWithAlignment(actualExtra),
        },
        layout: tableLayout1,
        color: '#071738',
        fontSize: 8,
        bold: true,
      });

      if (this.PlatformFeeEnabled) {
        // Saas Table
        let saasData = [
          ['Contracted Units', this.SaasContractedUnits],
          ['Platform Fee Per Unit', this.SaasUnitFee],
          [
            (this.SaasBillingCycle === 'Monthly' ? 'Monthly' : 'Annual') + ' Total Cost',
            '$' + formatNumberWithCommas(parseFloat(this.SaasTotal), 2),
          ],
        ];

        firstPageContent.push({
          absolutePosition: { x: 364, y: 110 },
          table: {
            widths: saasWidths,
            body: this.formatTableWithAlignment(saasData),
          },
          layout: tableLayoutSaas,
          color: '#071738',
          fontSize: 8,
          bold: true,
        });

        firstPageContent.push({
          absolutePosition: { x: 359, y: 97 },
          text: 'Billed ' + this.SaasBillingCycle,
          color: '#00ab9b',
          fontSize: 6,
          bold: false,
        });
      }

      // Paint Addons Table
      firstPageContent.push({
        absolutePosition: { x: 27, y: 443 },
        table: {
          widths: addonsWidths,
          body: this.formatTableWithAlignment(this.PaintAddons),
        },
        layout: tableLayout1,
        color: '#071738',
        fontSize: 8,
        bold: true,
      });

      // Conditionally add discount badge if it exists
      if (this.Discount && this.Discount !== '""') {
        firstPageContent.push({
          text: this.Discount,
          fontSize: 12,
          bold: true,
          color: '#ffffff',
          absolutePosition: { x: 190, y: 30 },
        });
      }

      secondPageContent.push([
        { text: '' },
        {
          table: {
            widths: page2Width,
            body: this.formatTableWithAlignment(this.Repair),
          },
          layout: tableLayout2,
          absolutePosition: { x: 210, y: 530 },
          color: '#071738',
          fontSize: 7,
          bold: true,
        },
        {
          table: {
            widths: page2Width,
            body: this.formatTableWithAlignment(this.Resurfacing),
          },
          layout: tableLayout2,
          absolutePosition: { x: 20, y: 473 },
          color: '#071738',
          fontSize: 7,
          bold: true,
        },
        {
          table: {
            widths: page2Width,
            body: this.formatTableWithAlignment(this.Maintenance),
          },
          layout: tableLayout2,
          absolutePosition: { x: 402, y: 90 },
          color: '#071738',
          fontSize: 7,
          bold: true,
        },

        {
          table: {
            widths: page2Width,
            body: this.formatTableWithAlignment(this.CarpetCleaning),
          },
          layout: tableLayout2,
          absolutePosition: { x: 211, y: 90 },
          color: '#071738',
          fontSize: 7,
          bold: true,
        },

        {
          table: {
            widths: page2Width,
            body: this.formatTableWithAlignment(this.Cleaning),
          },
          layout: tableLayout2,
          absolutePosition: { x: 402, y: 511 },
          color: '#071738',
          fontSize: 7,
          bold: true,
        },

        {
          table: {
            widths: page2Width,
            body: this.formatTableWithAlignment(this.Painting),
          },
          layout: tableLayout2,
          absolutePosition: { x: 20, y: 90 },
          color: '#071738',
          fontSize: 7,
          bold: true,
        },
        { text: '' },
      ]);

      // Create document definition
      const docDefinition: TDocumentDefinitions = {
        pageSize: 'A4',
        pageMargins: [20, 60, 20, 40],
        content: [
          // First page content
          {
            stack: firstPageContent,
          },

          // Page break
          { text: '', pageBreak: 'after' },

          {
            stack: secondPageContent,
          },
        ],
        styles: {
          header: {
            fontSize: 18,
            bold: true,
            margin: [0, 0, 0, 10],
          },
          tableStyle: {
            fontSize: 8,
            bold: true,
            color: '#071738',
          },
        },
        defaultStyle: {
          fontSize: 10,
          color: '#071738',
        },
        // Add background images if they loaded successfully
        background: function (currentPage) {
          if (currentPage === 1) {
            const bgKey = paintProvided ? 'bg1_paint' : 'bg1_nopaint';
            if (imageCache[bgKey]) {
              return {
                image: imageCache[bgKey],
                width: 595, // A4 width
              };
            }
          } else if (currentPage === 2 && imageCache['bg2']) {
            return {
              image: imageCache['bg2'],
              width: 595, // A4 width
            };
          }
          // Return null when no background is available
          return null;
        },
      };

      // Generate PDF
      const pdf = pdfMake.createPdf(docDefinition);

      if (openInNewTab) {
        // Open in a new tab
        pdf.open();
      } else {
        // Download the PDF
        pdf.download(exportName + '.pdf');
      }

      // Call the callback when done
      onExported();
    } catch (error) {
      console.error('Error generating PDF:', error);
      onExported();
    }
  }
}

export default PDFModel;
